import { useCallback, useEffect, useId, useRef, useState, type DragEvent, type ReactNode } from "react";
import { translateError, type VixioError } from "./errors";
import {
  acceptAttribute, checkUploadFile, formatBytes, uploadInTwoSteps,
  type PutObject, type UploadProgress, type UploadReservation, type UploadRule,
} from "./upload";

/**
 * Hook e componente únicos de envio de arquivo (portal, entregáveis, chamados, mídia do site): escolher ou arrastar,
 * conferir tipo e tamanho, reservar (RPC), subir com progresso, concluir (RPC), cancelar e tentar de novo.
 * Visual pelas classes do painel (vx-filepick, vx-progress, vx-meter, vx-btn).
 */

export type UploadStatus = "idle" | "uploading" | "done" | "error";

export interface UseFileUploadOptions<T extends UploadReservation, R> {
  rule: UploadRule;
  begin: (contentType: string, file: File) => Promise<T>;
  finish: (reservation: T, file: File) => Promise<R>;
  onDone?: (result: R, file: File) => void;
  put?: PutObject;
}

export interface FileUploadState<R> {
  status: UploadStatus;
  file: File | null;
  progress: UploadProgress | null;
  error: string | null;
  result: R | null;
  /** Envia o arquivo (resolve com o resultado, ou null se falhou ou foi cancelado). */
  start: (file: File) => Promise<R | null>;
  cancel: () => void;
  reset: () => void;
}

export function useFileUpload<T extends UploadReservation, R>(o: UseFileUploadOptions<T, R>): FileUploadState<R> {
  const [status, setStatus] = useState<UploadStatus>("idle");
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<R | null>(null);
  const abort = useRef<AbortController | null>(null);
  const opts = useRef(o);
  opts.current = o;
  useEffect(() => () => abort.current?.abort(), []);

  const start = useCallback(async (f: File): Promise<R | null> => {
    abort.current?.abort();
    const ctl = new AbortController();
    abort.current = ctl;
    setFile(f);
    setError(null);
    setResult(null);
    setProgress({ loaded: 0, total: f.size, pct: 0 });
    setStatus("uploading");
    try {
      const r = await uploadInTwoSteps(f, {
        rule: opts.current.rule, begin: opts.current.begin, finish: opts.current.finish, put: opts.current.put,
        signal: ctl.signal, onProgress: (p) => !ctl.signal.aborted && setProgress(p),
      });
      if (ctl.signal.aborted) return null;
      setResult(r);
      setStatus("done");
      opts.current.onDone?.(r, f);
      return r;
    } catch (e) {
      if (ctl.signal.aborted) return null;
      setError(translateError(e as VixioError).message);
      setStatus("error");
      return null;
    }
  }, []);

  const cancel = useCallback(() => {
    abort.current?.abort();
    setStatus("idle");
    setProgress(null);
    setFile(null);
  }, []);

  const reset = useCallback(() => {
    abort.current?.abort();
    setStatus("idle");
    setProgress(null);
    setFile(null);
    setError(null);
    setResult(null);
  }, []);

  return { status, file, progress, error, result, start, cancel, reset };
}

function UploadIcon() {
  return (
    <svg aria-hidden="true" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 13v8" />
      <path d="M4 14.9A7 7 0 1 1 15.7 8h1.8a4.5 4.5 0 0 1 2.5 8.2" />
      <path d="m8 17 4-4 4 4" />
    </svg>
  );
}

/** Barra de progresso do envio (acessível: role=progressbar com o valor). */
export function UploadProgressBar({ name, progress, label = "Enviando" }: { name: string; progress: UploadProgress | null; label?: string }) {
  const pct = progress?.pct ?? 0;
  return (
    <div className="vx-progress">
      <div className="vx-progress__label">
        <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
          {label} {name}
        </span>
        <span className="num">{pct}%</span>
      </div>
      <div className="vx-meter" role="progressbar" aria-label={`${label} ${name}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
        <span style={{ width: `${pct}%` }} />
      </div>
      {progress ? (
        <span className="vx-filepick__meta">
          {formatBytes(progress.loaded)} de {formatBytes(progress.total)}
        </span>
      ) : null}
    </div>
  );
}

export interface FileDropzoneProps {
  rule: UploadRule;
  /** Rótulo do campo. */
  label?: string;
  /** Texto principal da área. */
  prompt?: ReactNode;
  disabled?: boolean;
  onPick: (file: File) => void;
  /** Mensagem de erro externa (ex.: do envio). */
  error?: string | null;
  testId?: string;
}

/** Área para escolher o arquivo (clique, teclado ou arrastar e soltar), com os tipos e o limite. */
export function FileDropzone({ rule, label = "Arquivo", prompt, disabled, onPick, error, testId }: FileDropzoneProps) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const pick = (f: File | undefined | null) => {
    if (!f || disabled) return;
    onPick(f);
    if (input.current) input.current.value = "";
  };
  const onDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setOver(false);
    pick(e.dataTransfer.files?.[0]);
  };
  return (
    <div className="vx-field">
      <span className="vx-field__label" id={`${id}-label`}>
        {label}
      </span>
      <input
        ref={input}
        id={id}
        type="file"
        className="sr-only"
        accept={acceptAttribute(rule)}
        aria-labelledby={`${id}-label`}
        aria-describedby={`${id}-hint`}
        disabled={disabled}
        data-testid={testId}
        onChange={(e) => pick(e.target.files?.[0])}
      />
      <label
        htmlFor={id}
        className={`vx-filepick${over ? " vx-filepick--over" : ""}${error ? " vx-filepick--error" : ""}`}
        aria-disabled={disabled || undefined}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
      >
        <UploadIcon />
        <span>{prompt ?? <><strong>Escolha um arquivo</strong> ou arraste para cá</>}</span>
        <span className="vx-filepick__meta" id={`${id}-hint`}>
          {rule.label}, até {formatBytes(rule.maxBytes)}
        </span>
        {error ? (
          <span className="vx-filepick__meta" role="alert">
            {error}
          </span>
        ) : null}
      </label>
    </div>
  );
}

export interface UploadFieldProps<T extends UploadReservation, R> extends UseFileUploadOptions<T, R> {
  label?: string;
  prompt?: ReactNode;
  disabled?: boolean;
  /** Mensagem depois do envio (some no próximo arquivo). */
  doneMessage?: (file: File) => string;
  testId?: string;
}

/**
 * Campo completo: escolher/arrastar → confere → envia com progresso → conclui. Cancelar durante o envio; em caso de
 * erro, a mensagem fica no campo e "Tentar de novo" repete com o mesmo arquivo.
 */
export function UploadField<T extends UploadReservation, R>(p: UploadFieldProps<T, R>) {
  const up = useFileUpload<T, R>(p);
  const [pickError, setPickError] = useState<string | null>(null);
  const onPick = (f: File) => {
    const problem = checkUploadFile(f, p.rule);
    setPickError(problem);
    if (!problem) void up.start(f);
  };
  if (up.status === "uploading" && up.file) {
    return (
      <div className="vx-field" aria-live="polite">
        <span className="vx-field__label">{p.label ?? "Arquivo"}</span>
        <div className="vx-filepick vx-filepick--chosen" style={{ flexDirection: "column", alignItems: "stretch" }}>
          <UploadProgressBar name={up.file.name} progress={up.progress} />
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button type="button" className="vx-btn vx-btn--sm vx-btn--ghost" onClick={up.cancel}>
              Cancelar envio
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="vx-stack" style={{ gap: 6 }}>
      <FileDropzone
        rule={p.rule}
        label={p.label}
        prompt={p.prompt}
        disabled={p.disabled}
        onPick={onPick}
        error={pickError ?? (up.status === "error" ? up.error : null)}
        testId={p.testId}
      />
      {up.status === "error" && up.file ? (
        <div>
          <button type="button" className="vx-btn vx-btn--sm" onClick={() => up.file && void up.start(up.file)}>
            Tentar de novo
          </button>
        </div>
      ) : null}
      {up.status === "done" && up.file ? (
        <p className="vx-filepick__meta" role="status" style={{ margin: 0 }}>
          {p.doneMessage ? p.doneMessage(up.file) : `${up.file.name} enviado.`}
        </p>
      ) : null}
    </div>
  );
}
